import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from 'helpers/css';
import { Loader2Icon } from 'lucide-react';

const buttonVariants = cva(
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium  focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0',
  {
    variants: {
      variant: {
        default: 'tw-bg-primary tw-text-primary-foreground tw-shadow hover:tw-bg-primary/90',
        destructive:
          'tw-bg-destructive dark:tw-bg-red-600 tw-text-white tw-shadow-sm hover:tw-bg-destructive/90',
        outline:
          'tw-border tw-border-input tw-bg-background tw-shadow-sm hover:tw-bg-accent dark:hover:tw-bg-[var(--bg-dark-input)] hover:tw-text-accent-foreground dark:tw-bg-[var(--bg-dark)]',
        secondary:
          'tw-bg-secondary tw-text-secondary-foreground tw-shadow-sm hover:tw-bg-secondary/80',
        ghost: 'hover:tw-bg-accent hover:tw-text-accent-foreground',
        link: 'tw-text-primary tw-underline-offset-4 hover:tw-underline tw-text-btPrimary [&_svg]:tw-fill-btPrimary [&_svg]:tw-duration-300',
      },
      size: {
        default: 'tw-min-h-9 tw-px-4 tw-py-2',
        sm: 'tw-h-8 tw-rounded-md tw-px-3 tw-text-xs',
        lg: 'tw-h-10 tw-rounded-md tw-px-8',
        icon: 'tw-min-h-9 tw-w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, isLoading = false, ...props }, ref) => {
    const { children, disabled, type, ...restProps } = props;
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className: `${className} tw-relative` }))}
        disabled={isLoading || disabled}
        ref={ref}
        type={type || 'button'}
        {...restProps}
      >
        {isLoading && (
          <div className='tw-absolute tw-top-[50%] tw-left-[50%] tw-translate-x-[-50%] tw-translate-y-[-50%]'>
            <Loader2Icon className='tw-animate-spin' />
          </div>
        )}
        <div
          className={cn(
            'btn-content tw-inline-flex tw-gap-1 tw-items-center tw-flex-wrap',
            isLoading && 'tw-invisible',
          )}
        >
          {children}
        </div>
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
